import { Component, ViewChild, AfterViewInit, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { MatSort, Sort, MatSortHeader } from '@angular/material/sort';
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { tap, switchMap, filter, takeUntil } from 'rxjs/operators';
import { merge, timer, Subject } from 'rxjs';
import { cloneDeep } from 'lodash-es';
import { DriveDeliveryService } from '../../services';
import { FilterParameters, DriveDeliveryStatusViewModel } from './models';
import { Params, RouterLink, Router, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { SourceSystem } from '../../../../models/enums';
import { LoadingSpinnerComponent } from '../../../../shared/components/loading-spinner/loading-spinner.component';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatTable, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell } from '@angular/material/table';
import { DatePipe } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'insight-drive-delivery-status',
  templateUrl: './drive-delivery-status.component.html',
  styleUrls: ['./drive-delivery-status.component.scss'],
  imports: [LoadingSpinnerComponent, MatFormField, MatInput, FormsModule, ReactiveFormsModule, MatLabel, MatTooltipModule,
    RouterLink, MatIcon, MatTable, MatSort, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatColumnDef, MatHeaderCellDef,
    MatHeaderCell, MatSortHeader, MatCellDef, MatCell, DatePipe, MatCheckbox]
})
export class DriveDeliveryStatusComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatSort) sort: MatSort;

  filterServerNameText = new UntypedFormControl('');
  filterDriveText = new UntypedFormControl('');
  showOnlyDriveReady = new UntypedFormControl(true);
  columnSelect = new UntypedFormControl('');
  sortDirection = new UntypedFormControl('asc');
  currentDate = new Date();
  dataSource: DriveDeliveryStatusViewModel[];

  public isLoadingData = signal(false);

  displayedColumns: string[] = [
    'rushOrder',
    'serverName',
    'driveLetter',
    'publicityWritten',
    'driveReady',
    'lastUpdatedDate',
    'shipmentDate',
    'deliveryType',
    'notes'
  ];

  private filterValues: FilterParameters = {
    filter: undefined,
    filterDriveText: undefined,
  };

  private componentDestroyed$ = new Subject<void>();
  private organizeData$ = new Subject<void>();
  private originalData: DriveDeliveryStatusViewModel[];

  // Dependency injections
  private readonly driveDeliveryService = inject(DriveDeliveryService);
  private titleService = inject(Title);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  ngOnInit() {
    this.titleService.setTitle("Insight - Drive Delivery Status");

    // Initialize state from URL query parameters
    this.route.queryParams.pipe(
      takeUntil(this.componentDestroyed$)
    ).subscribe(params => {
      if (params['showOnlyReady'] === 'false') {
        this.showOnlyDriveReady.setValue(false, { emitEvent: false });
      }
    });
  }

  ngAfterViewInit(): void {
    this.isLoadingData.set(true);
    this.driveDeliveryService.getDeliveryStatuses().pipe(
      tap(response => {
        // initial setup
        this.dataSource = response;
        this.sort.disableClear = true;
        this.originalData = cloneDeep(response);
        this.initializeFilterAndSortStreams();
        this.isLoadingData.set(false);

        // Trigger query parameters loading to grab latest value
        if (this.showOnlyDriveReady.value) {
          this.organizeData$.next();
        }
      }),
      switchMap(() => timer(15000, 15000).pipe(
        tap(() => this.isLoadingData.set(true)),
        // switchMap (not mergeMap) so a response slower than the 15s tick is discarded
        // instead of racing the next one and writing stale data out of order
        switchMap(() => this.driveDeliveryService.getDeliveryStatuses()),
      )),
      takeUntil(this.componentDestroyed$)
    ).subscribe(response => {
      this.isLoadingData.set(false);
      this.originalData = cloneDeep(response);
      this.organizeData$.next();
    });
  }

  ngOnDestroy(): void {
    this.organizeData$.complete();
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }

  public orderShipmentQueryParams(order: DriveDeliveryStatusViewModel): Params {
    return {
      search: order.orderNumber,
      detailsOrderNumber: order.orderNumber,
      detailsShipmentNumber: order.shipmentNumber ?? 1,
      detailsSourceSystem: SourceSystem.Both
    };
  }

  public formatNotes(salesNote: string): string {
    // if the message says "was not recognized as a valid shipment", that really just means it is an empty drive
    // we also want to simplify it so it helps keep the table compact
    const isEmptyDrive = salesNote.includes('was not recognized as a valid shipment');
    if (isEmptyDrive) {
      return 'Drive is empty';
    }

    return salesNote;
  }

  public statusColor(statusState: boolean): string {
    if (statusState === true) {
      return 'success-state';
    }
    return 'failure-state';
  }

  private filterPredicate = (data: DriveDeliveryStatusViewModel): boolean => {
    if (this.filterValues.filter && this.filterValues.filter !== '') {
      const hasServerNameMatch = data.serverName.trim().toLowerCase().includes(this.filterValues.filter.trim().toLowerCase());
      if (!hasServerNameMatch) {
        return false;
      }
    }

    // separate filter text box for drive letter
    if (this.filterValues.filterDriveText && this.filterValues.filterDriveText !== '') {
      if (!this.filterValues.filterDriveText.trim().toLowerCase().includes(data.driveLetter.trim().toLowerCase())) {
        return false;
      }
    }

    if (this.showOnlyDriveReady.value && !data.driveReady) {
      return false;
    }

    return true;
  };

  private sortData(data: DriveDeliveryStatusViewModel[], column: string, direction: 'asc' | 'desc' | ''): DriveDeliveryStatusViewModel[] {
    let directionMultiplier = 1;

    if (direction === 'desc') {
      directionMultiplier = -1;
    }

    if (data.length === 0) {
      return data;
    }

    return data.sort((a, b) => {
      if ((a[column] === 0 || a[column] == null) || (b[column] === 0 || b[column] == null)) {
        if ((a[column] != null && a[column] !== 0) && (b[column] == null || b[column] === 0)) {
          return (-1 * directionMultiplier);
        } else if ((b[column] != null && b[column] !== 0) && (a[column] == null || a[column] === 0)) {
          return (1 * directionMultiplier);
        } else {
          return 0;
        }
      } else if (a[column] > b[column]) {
        return (1 * directionMultiplier);
      } else if (b[column] > a[column]) {
        return (-1 * directionMultiplier);
      } else {
        return 0;
      }
    });
  }

  /**
   * Wires up the filter/sort streams; called once from ngAfterViewInit after the
   * data source and MatSort are ready (was previously gated by a subject + nested subscribes).
   */
  private initializeFilterAndSortStreams(): void {
    this.columnSelect.valueChanges
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(value => {
        this.sort.active = value;
      });

    this.sortDirection.valueChanges
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(value => {
        this.sort.direction = value;
        this.sort.sortChange.next({ active: this.sort.active, direction: this.sort.direction });
        this.sort._stateChanges.next();
      });

    const filterChanges$ = this.filterServerNameText.valueChanges.pipe(
      tap(value => {
        if (value == null || value === '') {
          this.filterValues.filter = undefined;
        } else {
          this.filterValues.filter = value;
        }
      })
    );

    const filterDriveTextChanges$ = this.filterDriveText.valueChanges.pipe(
      tap(value => {
        if (value == null || value === '') {
          this.filterValues.filterDriveText = undefined;
        } else {
          this.filterValues.filterDriveText = value;
        }
      })
    );

    // Update URL query params
    const driveReadyChanges$ = this.showOnlyDriveReady.valueChanges.pipe(
      tap((value: string) => {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { showOnlyReady: value ? null : 'false' }, // only need to save if false since true is the default
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      })
    );

    const sortChanges$ = this.sort.sortChange.pipe(
      filter(event => event.active !== ''),
      tap((value: Sort) => {
        this.columnSelect.setValue(value.active, { emitEvent: false });
        this.sortDirection.setValue(value.direction, { emitEvent: false });
      })
    );

    merge(this.organizeData$, filterChanges$, filterDriveTextChanges$, driveReadyChanges$, sortChanges$)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        // this console.log() is for testing. There was suspected errors going on and causing the data
        // not to be updated.
        console.log('Data source is being updated (from either new data, filter changes, or sorting changes). This will cause the UI update');
        this.dataSource = this.originalData.filter(this.filterPredicate);
        this.dataSource = this.sortData(this.dataSource, this.sort.active, this.sort.direction);
      });
  }
}
